/**
 * **A gota corrosiva: uma marca por stack, desenhada de estado autoritativo.**
 *
 * ## Quem manda desenhar, e quem nunca manda
 *
 * Este módulo desenha o que a AUTORIDADE diz que existe. Ele não sabe o que é um
 * perfil, um socket, uma cópia, um nível, uma chance ou uma fórmula, e não tem
 * por onde saber: a sua única entrada é uma lista de alvos com
 * `{ key, x, y, diameter, stacks[] }` - onde o alvo está, que largura tem
 * DESENHADA, e que marcas ele carrega. Nem sequer a vida dele entra.
 *
 * A marca é uma coisa NO INIMIGO. Não é uma decoração do projétil, do canhão nem
 * da nave que atacou, e nada aqui toca em nenhum dos três.
 *
 * ## O pulso é DERIVADO, e não anunciado
 *
 * Cada stack traz quantas parcelas já pagou. Quando esse número sobe, a gota
 * daquele stack pulsa - uma vez, curta. Não há evento, não há protocolo e não há
 * mensagem: o facto já está no estado, e um canal só para o dizer seria um canal
 * a mais para uma decoração derivável.
 *
 * O nascimento pulsa da mesma maneira, pela mesma razão: uma gota que aparece é
 * uma mudança no estado.
 *
 * ## Um objeto gráfico por ALVO, e não um por marca
 *
 * Vinte stacks num mob são vinte gotas e **um** `Graphics`. As gotas vivem em
 * coordenadas LOCAIS desse objeto, portanto o alvo a mover-se custa um
 * `setPosition` por quadro em vez de um redesenho - e a geometria só é
 * recalculada quando o conjunto de stacks muda ou quando alguma gota está a
 * pulsar.
 *
 * Cada gota continua individualmente visível. Não há `+N`, não há número
 * agregado, não há limite silencioso, e o teto de gotas que este sistema desenha
 * é o número de stacks que a autoridade mandou - qualquer que ele seja.
 *
 * ## Sem textura, e é deliberado
 *
 * Uma gota é um polígono de trinta e poucos pontos, e o seu contorno UNITÁRIO é
 * calculado uma vez ao carregar o módulo. Gerar uma textura pouparia o
 * preenchimento e custaria uma imagem por gota - que é exatamente o objeto por
 * stack que este sistema existe para não criar.
 */

/* -------------------------------------------------------------------------- */
/*  A direção de arte                                                         */
/* -------------------------------------------------------------------------- */

/**
 * O núcleo: amarelo-esverdeado. O ponto mais claro da gota.
 *
 * Ele é o que a torna legível contra um mapa escuro sem a fazer brilhar: uma
 * gota inteira nesta cor leria-se como uma luz, e o que se quer é corrosão.
 */
const CORE = 0xd8f24a;

/** O corpo: verde-ácido, e a maior parte da silhueta. */
const BODY = 0x5fd12a;

/**
 * O contorno: verde-escuro.
 *
 * Desenhado como uma gota LIGEIRAMENTE maior por baixo das outras duas, e não
 * como um traço: um `lineStyle` teria espessura constante e tampas redondas na
 * ponta, que é precisamente o que faria a gota deixar de ter bico.
 */
const OUTLINE = 0x14421a;

/**
 * **Quanto da largura do alvo uma gota ocupa: 14 %.**
 *
 * O §"Tamanho-base da marca" do contrato, escrito uma vez e multiplicado pelo
 * diâmetro DESENHADO do alvo - que a cena mede na fonte por onde já o desenha.
 * Um tamanho fixo de viewport foi excluído pelo contrato, e com razão: uma gota
 * de 9 px sobre um boss lê-se como sujidade, e sobre um mob pequeno tapa o alvo.
 *
 * O zoom acompanha sozinho, porque o diâmetro que entra aqui já é o desenhado.
 */
export const POISON_MARK_TARGET_RATIO = 0.14;

/**
 * O tamanho MÍNIMO de uma gota, em píxeis da cena.
 *
 * Não é um teto disfarçado e não limita coisa nenhuma que um item conceda: é o
 * chão de legibilidade de uma forma com bico. Abaixo de quatro píxeis o bico e o
 * bolbo deixam de se distinguir e a gota lê-se como um ponto, que é a mesma
 * informação com pior leitura.
 */
export const POISON_MARK_MIN_PX = 4;

/**
 * A altura de uma gota, em múltiplos da sua largura. **Derivada** da geometria.
 *
 * O bolbo tem raio `r` e o bico está a {@link APEX_RATIO} raios acima do centro
 * dele, portanto a silhueta mede `(APEX_RATIO + 1) x r` de alto e `2 x r` de
 * largo. Escrever a razão à mão seria um número que deixa de estar certo no dia
 * em que o bico mudar.
 */
export const POISON_MARK_ASPECT = (2.3 + 1) / 2;

/** Quanto espaço horizontal separa duas gotas, em múltiplos da largura. */
const GAP_RATIO = 0.34;

/** E quanto separa duas linhas, em múltiplos da altura. */
const ROW_GAP_RATIO = 0.18;

/**
 * A vida de um pulso, em ms.
 *
 * Curta de propósito: o §"pulsa discretamente uma vez em cada tick" pede um
 * sinal e não uma animação. A duração é bem menor do que o segundo que separa
 * duas parcelas, portanto duas gotas nunca ficam presas a pulsar sem parar.
 */
export const POISON_PULSE_MS = 220;

/** Quanto uma gota cresce no pico do pulso. */
const PULSE_SCALE = 0.38;

/* -------------------------------------------------------------------------- */
/*  A geometria, calculada e não desenhada                                    */
/* -------------------------------------------------------------------------- */

/** A que distância o bico fica do centro do bolbo, em raios. */
const APEX_RATIO = 2.3;

export interface MarkPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * **A gota, como um polígono fechado de largura unitária.**
 *
 * Um bolbo circular em baixo e um BICO em cima, ligados pelas duas tangentes que
 * partem do bico e tocam o círculo. As tangentes são calculadas, e não
 * aproximadas: é isso que faz a junção entre a curva e a reta não ter canto, e é
 * isso que faz o bico ser agudo por construção em vez de por afinação.
 *
 * Devolvido com a caixa envolvente CENTRADA na origem e largura exatamente 1,
 * para que quem desenha só tenha de multiplicar por um tamanho e somar uma
 * posição.
 *
 * Separado de qualquer desenho de propósito: uma prova pode medir o bico, a
 * largura e a altura sem abrir um canvas.
 *
 * @param samples quantos pontos no arco do bolbo.
 */
export function poisonDropOutline(samples = 28): MarkPoint[] {
  const r = 0.5;
  const d = APEX_RATIO * r;
  /*
   * O ângulo da tangente, medido a partir da direção que vai do centro do bolbo
   * para o bico. `cos(alpha) = r / d` é a relação do triângulo retângulo que o
   * raio, a tangente e a linha centro-bico formam.
   */
  const alpha = Math.acos(r / d);
  const apex = { x: 0, y: -d };
  const right = { x: r * Math.sin(alpha), y: -r * Math.cos(alpha) };
  const left = { x: -r * Math.sin(alpha), y: -r * Math.cos(alpha) };
  const fromAngle = Math.atan2(right.y, right.x);
  /* O caminho LONGO, que passa por baixo do bolbo. */
  const toAngle = Math.atan2(left.y, left.x) + Math.PI * 2;

  /*
   * **Os três ângulos EXATOS, forçados na amostragem.**
   *
   * `0`, `pi/2` e `pi` são onde o bolbo atinge os seus extremos - direita, fundo
   * e esquerda. Uma amostragem uniforme passa ao lado deles, e a silhueta sai
   * com 0,99986 de largura em vez de 1: a diferença é invisível no ecrã e é
   * FATAL para o contrato, porque "14 % do diâmetro do alvo" deixa de ser uma
   * igualdade e passa a ser uma aproximação que ninguém pode afirmar.
   *
   * Incluí-los custa três pontos e torna a largura exatamente `2r` e a altura
   * exatamente `d + r`, por construção.
   */
  const angles = new Set<number>();
  for (let i = 0; i <= samples; i += 1) {
    angles.add(fromAngle + ((toAngle - fromAngle) * i) / samples);
  }
  angles.add(0);
  angles.add(Math.PI / 2);
  angles.add(Math.PI);

  const points: MarkPoint[] = [apex];
  for (const angle of [...angles].sort((left_, right_) => left_ - right_)) {
    points.push({ x: Math.cos(angle) * r, y: Math.sin(angle) * r });
  }
  /*
   * Centrar a caixa envolvente. Ela vai de `-d` a `+r` em y, portanto o centro
   * está em `(r - d) / 2` e desloca-se o oposto disso.
   */
  const shift = (d - r) / 2;
  return points.map((point) => ({ x: point.x, y: point.y + shift }));
}

/** O contorno unitário, calculado UMA vez ao carregar o módulo. */
const UNIT_DROP = poisonDropOutline();

/**
 * **Onde cada gota fica**, em coordenadas locais da âncora.
 *
 * A âncora é o topo da barra de vida do alvo, portanto tudo aqui tem `y`
 * NEGATIVO: as marcas ficam acima dela e nunca sobre ela, sobre o casco, sobre
 * um nome ou sobre um marcador de equipa.
 *
 * As linhas enchem-se de baixo para cima e cada uma é CENTRADA, portanto sete
 * gotas numa largura de cinco dão uma linha de cinco e outra de duas, as duas
 * centradas sobre o alvo. A capacidade de cada linha sai da largura desenhada do
 * alvo - não de um número escolhido - por isso um boss põe mais por linha e um
 * mob pequeno põe menos, sem que ninguém decida nada.
 *
 * Nunca devolve menos posições do que gotas: não há teto, não há `+N`, e um
 * conjunto grande produz mais linhas em vez de menos marcas.
 */
export function poisonMarkLayout(count: number, markWidth: number, maxWidth: number): MarkPoint[] {
  if (count <= 0 || markWidth <= 0) {
    return [];
  }
  const step = markWidth * (1 + GAP_RATIO);
  const perRow = Math.max(1, Math.floor(maxWidth / step));
  const height = markWidth * POISON_MARK_ASPECT;
  const rowStep = height * (1 + ROW_GAP_RATIO);
  const places: MarkPoint[] = [];
  for (let index = 0; index < count; index += 1) {
    const row = Math.floor(index / perRow);
    const column = index % perRow;
    const inRow = Math.min(perRow, count - row * perRow);
    const rowWidth = (inRow - 1) * step;
    places.push({
      x: column * step - rowWidth / 2,
      /*
       * Meia altura para a primeira linha ficar INTEIRA acima da âncora, e
       * `rowStep` por cada linha acima dela.
       */
      y: -(height / 2) - row * rowStep,
    });
  }
  return places;
}

/**
 * **Quanto espaço as gotas ocupam ACIMA da âncora**, em píxeis da cena.
 *
 * Existe para o gate V28G.N: as marcas de sangramento têm de ocupar uma FAIXA
 * DIFERENTE, e a única forma de a garantir sem afinar um número à mão é a cena
 * perguntar a esta faixa qual é a altura dela.
 *
 * Puro e derivado de {@link poisonMarkLayout}: nunca uma segunda fórmula. Zero
 * quando não há gota nenhuma, que é o que faz a faixa de cima descer ao lugar da
 * de baixo quando o veneno acaba.
 *
 * Não muda nada do que o veneno desenha - é uma leitura, e não uma decisão.
 */
export function poisonBandHeight(count: number, markWidth: number, maxWidth: number): number {
  const places = poisonMarkLayout(count, markWidth, maxWidth);
  if (places.length === 0) {
    return 0;
  }
  const height = markWidth * POISON_MARK_ASPECT;
  let highest = 0;
  for (const place of places) {
    highest = Math.max(highest, -place.y + height / 2);
  }
  return highest;
}

/* -------------------------------------------------------------------------- */
/*  A cena, pela interface mínima                                             */
/* -------------------------------------------------------------------------- */

export interface PoisonMarkGraphics {
  clear(): PoisonMarkGraphics;
  fillStyle(color: number, alpha?: number): PoisonMarkGraphics;
  fillPoints(
    points: readonly MarkPoint[],
    closeShape?: boolean,
    closePath?: boolean,
  ): PoisonMarkGraphics;
  setDepth(value: number): PoisonMarkGraphics;
  setVisible(value: boolean): PoisonMarkGraphics;
  setPosition(x: number, y: number): PoisonMarkGraphics;
  destroy(): void;
}

export interface PoisonMarkScene {
  add: { graphics(config?: unknown): PoisonMarkGraphics };
}

/* -------------------------------------------------------------------------- */
/*  O que a cena entrega                                                      */
/* -------------------------------------------------------------------------- */

/** Um stack, reduzido ao que uma marca precisa. */
export interface PoisonMarkStack {
  /** A identidade autoritativa. É ela que faz a gota deste quadro ser a mesma. */
  readonly id: number;
  /** Quantas parcelas já foram cobradas. Cada subida é um pulso. */
  readonly ticks: number;
}

/** Um alvo envenenado, já em píxeis da cena. */
export interface PoisonMarkTarget {
  /** A identidade do alvo. Um mob por id, um casco por assento. */
  readonly key: string;
  /** O centro horizontal do alvo. */
  readonly x: number;
  /** O topo da barra de vida. As marcas ficam ACIMA disto. */
  readonly y: number;
  /**
   * O diâmetro DESENHADO do alvo, em píxeis da cena.
   *
   * A cena mede-o na fonte que ela própria já usa para o dimensionar. Este
   * módulo não sabe o que é uma nave, um mob, um boss ou um zoom - recebe um
   * comprimento.
   */
  readonly diameter: number;
  readonly stacks: readonly PoisonMarkStack[];
}

/* -------------------------------------------------------------------------- */
/*  O sistema                                                                 */
/* -------------------------------------------------------------------------- */

interface LiveMark {
  ticks: number;
  /** Quando o pulso atual começou, ou `null` se não há nenhum. */
  pulseAt: number | null;
}

interface LiveTarget {
  readonly graphics: PoisonMarkGraphics;
  readonly marks: Map<number, LiveMark>;
  /** O que foi desenhado da última vez. Um redesenho só acontece se isto mudar. */
  drawn: string;
}

/**
 * **O sistema.** Um por cena, dono de tudo o que cria.
 *
 * Nada aqui é global e nada sobrevive a `destroy()`: os objetos gráficos são
 * deste sistema, não há tween registado - o pulso é redesenhado a partir do
 * relógio que a cena entrega - e não há textura no gestor da cena.
 */
export class PoisonMarkSystem {
  private readonly targets = new Map<string, LiveTarget>();
  private disposed = false;

  constructor(
    private readonly scene: PoisonMarkScene,
    private readonly depth = 42,
  ) {}

  /**
   * Põe o ecrã de acordo com o estado autoritativo deste quadro.
   *
   * ## O que acontece a cada chamada, e o que não acontece
   *
   * Cada alvo é REPOSICIONADO - um `setPosition`, sempre, porque ele move-se - e
   * só é REDESENHADO quando a assinatura do que ele mostra mudou: o conjunto de
   * ids, o tamanho de uma gota, a largura disponível, ou a fase de um pulso em
   * curso. Um mob envenenado parado entre parcelas não custa geometria nenhuma.
   *
   * Um alvo que desapareceu da lista larga o seu objeto gráfico: morreu, ou o
   * último stack expirou. As duas coisas apagam a marca no mesmo quadro, que é o
   * §"desaparece imediatamente depois do sexto tick".
   *
   * @param nowMs o relógio da CENA. Nunca `Date.now()`: um pulso é apresentação,
   *   e a cena é quem sabe quando o seu próprio tempo pausou.
   */
  sync(targets: readonly PoisonMarkTarget[], nowMs: number): void {
    if (this.disposed) {
      return;
    }
    const seen = new Set<string>();
    for (const target of targets) {
      if (target.stacks.length === 0) {
        continue;
      }
      seen.add(target.key);
      this.syncTarget(target, nowMs);
    }
    for (const [key, live] of this.targets) {
      if (!seen.has(key)) {
        live.graphics.destroy();
        this.targets.delete(key);
      }
    }
  }

  /** Quantos alvos têm marca agora. Para as provas e para as medições. */
  liveCount(): number {
    return this.targets.size;
  }

  /** Quantas gotas estão desenhadas ao todo, somando todos os alvos. */
  markCount(): number {
    let total = 0;
    for (const live of this.targets.values()) {
      total += live.marks.size;
    }
    return total;
  }

  /**
   * Apaga tudo o que está no ecrã, sem o sistema deixar de funcionar.
   *
   * Para uma CORREÇÃO ou uma substituição de mundo: o que estava desenhado
   * pertencia a um estado que já não existe, e o próximo `sync` desenha o novo.
   */
  clear(): void {
    for (const live of this.targets.values()) {
      live.graphics.destroy();
    }
    this.targets.clear();
  }

  /** Larga tudo. Depois disto o sistema não desenha mais nada. */
  destroy(): void {
    this.disposed = true;
    this.clear();
  }

  private syncTarget(target: PoisonMarkTarget, nowMs: number): void {
    let live = this.targets.get(target.key);
    if (!live) {
      const graphics = this.scene.add.graphics();
      graphics.setDepth(this.depth).setVisible(true);
      live = { graphics, marks: new Map(), drawn: '' };
      this.targets.set(target.key, live);
    }
    /* A posição, SEMPRE. O alvo move-se e a marca vai com ele. */
    live.graphics.setPosition(target.x, target.y);

    /*
     * O que mudou desde o quadro anterior.
     *
     * Uma gota nova nasce a pulsar; uma gota cuja contagem subiu volta a pulsar;
     * uma gota que saiu da lista autoritativa deixa de existir. Nada aqui decide
     * se um stack existe - isso já foi decidido, e chegou pronto.
     */
    const nextMarks = new Map<number, LiveMark>();
    for (const stack of target.stacks) {
      const previous = live.marks.get(stack.id);
      if (previous === undefined) {
        nextMarks.set(stack.id, { ticks: stack.ticks, pulseAt: nowMs });
        continue;
      }
      if (stack.ticks > previous.ticks) {
        nextMarks.set(stack.id, { ticks: stack.ticks, pulseAt: nowMs });
        continue;
      }
      nextMarks.set(stack.id, previous);
    }
    live.marks.clear();
    for (const [id, mark] of nextMarks) {
      live.marks.set(id, mark);
    }

    const width = Math.max(POISON_MARK_MIN_PX, target.diameter * POISON_MARK_TARGET_RATIO);
    const places = poisonMarkLayout(target.stacks.length, width, Math.max(width, target.diameter));

    /*
     * A ASSINATURA do que se vai ver.
     *
     * Ids, tamanho, largura disponível, e a fase de cada pulso arredondada a um
     * inteiro - o que faz um pulso em curso mudar a assinatura a cada quadro e
     * uma gota parada não a mudar de todo. É isto que decide se há redesenho.
     */
    const signature = this.signatureOf(target, live, width, nowMs);
    if (signature === live.drawn) {
      return;
    }
    live.drawn = signature;

    const graphics = live.graphics;
    graphics.clear();
    target.stacks.forEach((stack, index) => {
      const place = places[index];
      if (place === undefined) {
        return;
      }
      const mark = live.marks.get(stack.id);
      const scale = width * (1 + this.pulseOf(mark, nowMs) * PULSE_SCALE);
      /*
       * Três camadas, de fora para dentro: o contorno escuro, o corpo ácido, e o
       * núcleo. O contorno é uma gota MAIOR por baixo em vez de um traço, porque
       * um traço tem espessura constante e tampas redondas - e a tampa redonda
       * na ponta é exatamente o que faria a gota deixar de ter bico.
       */
      this.drop(graphics, OUTLINE, 1, place, scale * 1.22, 0);
      this.drop(graphics, BODY, 1, place, scale, 0);
      /*
       * O núcleo vive no BOLBO e não no eixo da forma: metade do tamanho, e
       * descido para o centro da parte redonda, que é onde uma gota real teria o
       * seu brilho.
       */
      this.drop(graphics, CORE, 1, place, scale * 0.44, scale * 0.2);
    });
  }

  /** Uma gota, escalada e colocada. Nunca esticada: um só fator nos dois eixos. */
  private drop(
    graphics: PoisonMarkGraphics,
    color: number,
    alpha: number,
    place: MarkPoint,
    scale: number,
    offsetY: number,
  ): void {
    const points = UNIT_DROP.map((point) => ({
      x: place.x + point.x * scale,
      y: place.y + point.y * scale + offsetY,
    }));
    graphics.fillStyle(color, alpha).fillPoints(points, true, true);
  }

  /**
   * A intensidade do pulso desta gota, de 0 a 1.
   *
   * Sobe depressa e desce devagar - `sin` sobre a fração de vida do pulso - para
   * o sinal ser um toque e não uma respiração. Fora do pulso é exatamente zero,
   * que é o que faz uma gota parada não pedir redesenho nenhum.
   *
   * PURA: chamada duas vezes por quadro - uma para a assinatura e outra para o
   * desenho - e as duas têm de dar o mesmo número. Uma versão que limpasse o
   * pulso ao expirar faria a segunda leitura discordar da primeira.
   */
  private pulseOf(mark: LiveMark | undefined, nowMs: number): number {
    if (mark === undefined || mark.pulseAt === null) {
      return 0;
    }
    const elapsed = nowMs - mark.pulseAt;
    if (elapsed < 0 || elapsed >= POISON_PULSE_MS) {
      return 0;
    }
    return Math.sin((elapsed / POISON_PULSE_MS) * Math.PI);
  }

  private signatureOf(
    target: PoisonMarkTarget,
    live: LiveTarget,
    width: number,
    nowMs: number,
  ): string {
    const parts: string[] = [width.toFixed(2), target.diameter.toFixed(2)];
    for (const stack of target.stacks) {
      const mark = live.marks.get(stack.id);
      /*
       * A fase em CENTÉSIMOS. Fina o suficiente para o pulso se ler como
       * contínuo, e grossa o suficiente para uma gota parada dar sempre a mesma
       * string.
       */
      parts.push(`${stack.id}:${Math.round(this.pulseOf(mark, nowMs) * 100)}`);
    }
    return parts.join('|');
  }
}
